import type { EventEditDto, EventStateChangeDto, EventSyncChangeDto } from '@lib/dto/event.dto'
import { CANCELLATION_REASON_LABELS } from '@lib/enums/cancellation-reason.enum'
import { LOST_REASON_LABELS } from '@lib/enums/lost-reason.enum'

/**
 * One change to an event's status as a sentence: what happened, and why when
 * there was a reason. The stored words (`enquiry`, `cancelled`) never reach the
 * screen.
 */
export function stateChangeSentence(change: EventStateChangeDto): string {
  const { from, to } = change
  if (from === null) return to === 'enquiry' ? 'Created as an inquiry' : 'Created as a booking'
  if (to === 'booked') return from === 'cancelled' ? 'Restored' : 'Booked'
  if (to === 'cancelled') {
    return change.reasonCode === null ? 'Canceled' : `Canceled · ${CANCELLATION_REASON_LABELS[change.reasonCode]}`
  }
  if (to === 'lost') return change.lostReason === null ? 'Marked lost' : `Marked lost · ${LOST_REASON_LABELS[change.lostReason]}`
  return 'Reopened'
}

/** What the sync notes on an event it made: the event's own first status change says it, by HoneyBook. */
const MADE_IN_HONEYBOOK = 'Booked in HoneyBook'

/**
 * One change HoneyBook made to an event it manages, as a sentence. Its moves
 * to canceled and back, and making the event, show as status changes, so they
 * aren't repeated here.
 */
export function syncChangeSentence(change: EventSyncChangeDto): string | null {
  const from = change.before === null ? '' : ` from ${change.before}`
  switch (change.field) {
    case 'handed_over':
      return change.after === MADE_IN_HONEYBOOK ? null : change.after
    case 'name':
      return `HoneyBook renamed it${from} to ${change.after ?? 'nothing'}`
    case 'date':
      return change.before === null ? `HoneyBook dated it ${change.after ?? ''}` : `HoneyBook moved it${from} to ${change.after ?? ''}`
    case 'guest_count':
      return `HoneyBook changed the guests${from} to ${change.after ?? '0'}`
    case 'lifecycle':
      return null
    case 'times':
    case 'clients':
    case 'released':
      return change.after
    default: {
      const unexpected: never = change.field
      return unexpected
    }
  }
}

/** A change somebody made by hand, as a sentence; who made it is said beside it. */
export function editSentence(edit: EventEditDto): string {
  switch (edit.field) {
    case 'guest_count':
      if (edit.before === null) return `Set the guest count to ${edit.after ?? '0'}`
      if (edit.after === null) return `Cleared the guest count, which was ${edit.before}`
      return `Changed the guest count from ${edit.before} to ${edit.after}`
    // Written "Company · Service" when the change is made. One taken off by
    // something done elsewhere (the venue stopping work with it, say) also
    // says why, as a cancellation says its reason.
    case 'vendor_added':
      return `Added ${edit.after ?? 'a vendor'}`
    case 'vendor_removed': {
      const took = `Took ${edit.before ?? 'a vendor'} off the event`
      return edit.after === null ? took : `${took} · ${edit.after}`
    }
    default: {
      const unexpected: never = edit.field
      return unexpected
    }
  }
}

/** When, at the venue's clock where it has one. */
function when(at: string, timeZone: string | null): string {
  return new Date(at).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(timeZone === null ? {} : { timeZone }),
  })
}

interface EventHistoryProps {
  changes: readonly EventStateChangeDto[]
  /** What HoneyBook changed, for an event it manages. */
  honeybook?: readonly EventSyncChangeDto[] | undefined
  /** What people changed by hand: the guest count, set by staff, the couple or their planner, and the vendors. */
  edits?: readonly EventEditDto[] | undefined
  /** The venue's time zone; null shows the viewer's own clock. */
  timeZone: string | null
}

/**
 * Who changed an event's status, when and why, oldest first: booked, canceled,
 * restored, marked lost, reopened. The API has kept this since cancellations
 * arrived; this is where it is shown. Guest count and vendor changes join it,
 * because the couple and their planner can now make them.
 */
export function EventHistory({ changes, honeybook = [], edits = [], timeZone }: EventHistoryProps) {
  const madeInHoneyBook = honeybook.some((c) => c.field === 'handed_over' && c.after === MADE_IN_HONEYBOOK)
  const byHoneyBook = (change: EventStateChangeDto): boolean => change.reasonCode === 'honeybook' || (change.from === null && madeInHoneyBook)
  const rows = [
    ...changes.map((change) => ({
      at: change.at,
      sentence: stateChangeSentence(change),
      // Nobody did what HoneyBook did.
      by: change.byName ?? (byHoneyBook(change) ? 'HoneyBook' : 'Someone'),
      note: change.note,
    })),
    ...edits.map((edit) => ({ at: edit.at, sentence: editSentence(edit), by: edit.byName ?? 'Someone', note: null })),
    ...honeybook.flatMap((change) => {
      const sentence = syncChangeSentence(change)
      return sentence === null ? [] : [{ at: change.at, sentence, by: 'HoneyBook', note: null }]
    }),
  ].sort((a, b) => a.at.localeCompare(b.at))
  return (
    <ol className="m-0 list-none divide-y divide-line p-0">
      {rows.map((row, i) => (
        <li key={`${row.at}-${String(i)}`} className="py-2.5">
          <p className="m-0 text-sm text-ink">{row.sentence}</p>
          <p className="m-0 mt-0.5 text-xs text-ink-muted">
            {when(row.at, timeZone)} · {row.by}
          </p>
          {row.note ? <p className="m-0 mt-1 whitespace-pre-line text-sm text-ink-muted">{row.note}</p> : null}
        </li>
      ))}
    </ol>
  )
}
